import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, IconButton, SxProps, Theme, Tooltip, alpha, useTheme } from "@mui/material";
import type { MouseEvent } from "react";
import {
  openWorkOrderInDesktop,
  shouldOfferDualWorkOrderOpen,
} from "../Utils/webViewBridge";

export interface WorkOrderOpenIconButtonsProps {
  invoiceDocId: number;
  onOpenInServiceQueue: (invoiceDocId: number) => void;
  disabled?: boolean;
  iconFontSize?: string | number;
  stopPropagation?: boolean;
  iconButtonSx?: SxProps<Theme>;
}

const WorkOrderOpenIconButtons = ({
  invoiceDocId,
  onOpenInServiceQueue,
  disabled = false,
  iconFontSize = "0.875rem",
  stopPropagation = true,
  iconButtonSx,
}: WorkOrderOpenIconButtonsProps) => {
  const theme = useTheme();
  const isDesktopEmbed = shouldOfferDualWorkOrderOpen();

  const handleClick = (
    event: MouseEvent<HTMLButtonElement>,
    label: string,
    action: () => void,
  ) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    if (disabled) {
      console.warn(`[WorkOrderOpenIconButtons] ${label} blocked: disabled`);
      return;
    }
    console.log(
      `[WorkOrderOpenIconButtons] ${label} invoiceDocId=${invoiceDocId}`,
      `isDesktopEmbed=${isDesktopEmbed}`,
      `chrome.webview exists=${!!window.chrome?.webview}`,
      `postMessage type=${typeof window.chrome?.webview?.postMessage}`,
    );
    action();
  };

  const serviceQueueButton = (
    <Tooltip
      title={isDesktopEmbed ? "Open in Service Queue" : "Open Work Order"}
      arrow
      placement="top"
    >
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={(event) =>
            handleClick(event, "ServiceQueue", () =>
              onOpenInServiceQueue(invoiceDocId),
            )
          }
          sx={{
            color: theme.palette.info.main,
            "&:hover": {
              bgcolor: alpha(theme.palette.info.main, 0.1),
            },
            ...iconButtonSx,
          }}
        >
          <OpenInNewIcon sx={{ fontSize: iconFontSize }} />
        </IconButton>
      </span>
    </Tooltip>
  );

  if (!isDesktopEmbed) {
    return serviceQueueButton;
  }

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, pr: 0.02 }}>
      {serviceQueueButton}
      <Tooltip title="Open in POS" arrow placement="top">
        <span>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={(event) =>
              handleClick(event, "POS/Desktop", () =>
                openWorkOrderInDesktop(invoiceDocId),
              )
            }
            sx={{
              color: theme.palette.primary.main,
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
              ...iconButtonSx,
            }}
          >
            <DesktopWindowsOutlinedIcon sx={{ fontSize: iconFontSize }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default WorkOrderOpenIconButtons;
